import { useEffect, useState } from "react";
import CommonStyles from "@components/CommonStyles";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import CommonIcons from "@base/assets/icons";
import { useTranslation } from "react-i18next";
import { Divider, FloatButton } from "antd";
import { baseRoutes } from "@base/routes/baseRoutes";
import themeColor from "@base/themes/colorTheme";
import useLogout from "@base/modules/auth/hooks/useLogout";
import useToggle from "@base/hooks/useToggle";
import { useSelector } from "react-redux";
import { RootState } from "@base/store/store";
import { BreadcrumbItemType, BreadcrumbSeparatorType } from "antd/es/breadcrumb/Breadcrumb";
import AvatarUploader from "@components/User/Account/AvatarUploader";
import { CommentOutlined, CustomerServiceOutlined, EditOutlined } from "@ant-design/icons";
import { responseUI } from "@base/themes/response";

export default function User() {
  //! state
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userInfo = useSelector((state: RootState) => state.appCommonSlice.userInfo);
  const { isLoading: isLoggingOut, handleLogout } = useLogout();

  const { open, toggle } = useToggle();

  const [breadItems, setBreadItems] = useState<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[]>([]);
  const { id, key } = useParams();

  //! function
  useEffect(() => {
    const defaultBreadItems: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] = [
      {
        href: "/",
        title: <CommonIcons.IconHome />,
      },
    ];

    // user
    if (location.pathname.startsWith("/user")) {
      defaultBreadItems.push({
        href: "/user",
        title: t("user.user"),
      });
    }

    if (location.pathname.startsWith("/user/profile")) {
      defaultBreadItems.push({
        href: "/user/profile",
        title: t("user.menu.profile"),
      });
    }

    if (location.pathname.startsWith("/user/profile/profile-handler") && id === "0") {
      defaultBreadItems.push({
        href: "/user/profile/profile-handler",
        title: t("user.profile.labelNew"),
      });
    }

    if (location.pathname.startsWith("/user/profile/profile-handler") && id !== "0") {
      defaultBreadItems.push({
        href: "/user/profile/profile-handler",
        title: t("user.profile.labelDetail"),
      });
    }

    if (location.pathname.startsWith("/user/profile/profile-sync") && id !== "0") {
      defaultBreadItems.push({
        href: "/user/profile/profile-sync",
        title: t("user.profile.syncFromSite.title"),
      });
    }

    if (location.pathname.startsWith("/user/appointment")) {
      defaultBreadItems.push({
        href: "/user/appointment",
        title: t("user.menu.appointment"),
      });
    }

    if (location.pathname.startsWith("/user/appointment/appointment-detail") && key !== "0") {
      defaultBreadItems.push({
        href: "/user/appointment/appointment-detail",
        title: t("user.appointment.appointmentDetail"),
      });
    }

    if (location.pathname.startsWith("/user/prescription")) {
      defaultBreadItems.push({
        href: "/user/prescription",
        title: t("user.menu.prescription"),
      });
    }

    if (location.pathname.startsWith("/user/prescription/prescription-detail")) {
      defaultBreadItems.push({
        href: "/user/prescription/prescription-detail",
        title: "Chi tiết đơn thuốc",
      });
    }

    if (location.pathname.startsWith("/user/package")) {
      defaultBreadItems.push({
        href: "/user/package",
        title: t("user.menu.package"),
      });
    }

    if (location.pathname.startsWith("/user/package/package-detail") && key !== "0") {
      defaultBreadItems.push({
        href: "/user/package/package-detail",
        title: t("user.servicePackage.servicePackageDetail"),
      });
    }

    // if (location.pathname.startsWith("/user/promotion")) {
    //   defaultBreadItems.push({
    //     href: "/user/promotion",
    //     title: t("user.menu.promotion"),
    //   });
    // }

    if (location.pathname.startsWith("/user/examination")) {
      defaultBreadItems.push({
        href: "/user/examination",
        title: t("user.menu.examination"),
      });
    }

    if (location.pathname.startsWith("/user/insurance")) {
      defaultBreadItems.push({
        href: "/user/insurance",
        title: t("user.menu.insurance"),
      });
    }

    if (location.pathname.startsWith("/user/chronic")) {
      defaultBreadItems.push({
        href: "/user/chronic",
        title: t("user.menu.chronic"),
      });
    }

    if (location.pathname.startsWith("/user/payment")) {
      defaultBreadItems.push({
        href: "/user/payment",
        title: t("user.menu.payment"),
      });
    }

    if (location.pathname.startsWith("/user/payment/payment-detail") && key !== "0") {
      defaultBreadItems.push({
        href: "/user/payment/payment-detail",
        title: t("user.payment.paymentDetail"),
      });
    }

    if (location.pathname.startsWith("/user/change-password")) {
      defaultBreadItems.push({
        href: "/user/change-password",
        title: t("user.menu.changePassword"),
      });
    }

    setBreadItems(defaultBreadItems);
  }, [location.pathname, t]);

  const checkLinkActive = (pathName: string | undefined): boolean => {
    return !!pathName && location.pathname.startsWith(pathName);
  };

  const openLogoutConfirm = () => {
    if (isLoggingOut) {
      return;
    }

    toggle();
  };

  const onLogout = () => {
    toggle();

    // Let the modal finish closing before the page is replaced.
    requestAnimationFrame(handleLogout);
  };

  //! render
  return (
    <div className={`${responseUI.screen} ${responseUI.padding} pb-5 grid grid-cols-4 gap-x-8 gap-y-5`}>
      <div className="col-span-4 mt-5">
        <CommonStyles.BreadcrumbUI items={breadItems} />
      </div>

      {/* Header and footer stay put; only the menu between them scrolls once it outgrows the card. */}
      <div className="bg-white p-8 rounded-lg max-h-[610px] overflow-hidden max-xl:hidden flex flex-col">
        <div className="flex items-center gap-4 shrink-0">
          <AvatarUploader />

          <div className="min-w-0 flex flex-col">
            <div
              className="text-common-blue500 text-[18px] leading-[27px] font-['Inter'] hover:underline cursor-pointer line-clamp-1"
              title={userInfo?.user_profiles?.display_name ? userInfo?.user_profiles?.display_name : "---"}
              onClick={() => navigate(baseRoutes.user)}
            >
              {userInfo?.user_profiles?.display_name ? userInfo?.user_profiles?.display_name : "---"}
              {/* <EditOutlined className="text-common-primary" /> */}
            </div>
            <div className="text-common-subtext text-sm font-['Inter']">{userInfo?.user_name}</div>
          </div>
        </div>

        <Divider className="my-4 shrink-0" />

        <div className="flex-1 min-h-0 overflow-y-auto grid grid-flow-row content-start gap-4">
          <NavLink to={baseRoutes.userProfile}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconProfile />}
              text={t("user.menu.profile")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userProfile)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userAppointment}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconCalendarFill />}
              text={t("user.menu.appointment")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userAppointment)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userPrescription}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconMedication />}
              text={t("user.menu.prescription")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userPrescription)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userPackage}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconHealthcare />}
              text={t("user.menu.package")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userPackage)}
            />
          </NavLink>

          {/* <NavLink to={baseRoutes.userPromotion}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconVoucher />}
              text={t("user.menu.promotion")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userPromotion)}
            />
          </NavLink> */}

          <NavLink to={baseRoutes.userInsurance}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconInsurance />}
              text={t("user.menu.insurance")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userInsurance)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userExamination}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconTextSnippet />}
              text={t("user.menu.examination")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userExamination)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userChronic}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconMonitorHeart />}
              text={t("user.menu.chronic")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userChronic)}
            />
          </NavLink>

          <NavLink to={baseRoutes.userPayment}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconHistory />}
              text={t("user.menu.payment")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userPayment)}
            />
          </NavLink>
        </div>

        <Divider className="my-4 shrink-0" />

        <div className="grid grid-flow-row gap-4 shrink-0">
          <NavLink to={baseRoutes.userChangePassword}>
            <CommonStyles.NavLinkTextUI
              icon={<CommonIcons.IconLock />}
              text={t("user.menu.changePassword")}
              subColor={themeColor.subtext}
              isActive={checkLinkActive(baseRoutes.userChangePassword)}
            />
          </NavLink>

          <div className="flex items-center gap-3 text-common-negative cursor-pointer" onClick={openLogoutConfirm}>
            <CommonIcons.IconLogout />
            <span>{t("user.menu.logout")}</span>
          </div>
        </div>
      </div>

      <div className="col-span-3 max-xl:col-span-4 bg-white rounded-lg">
        <Outlet />
      </div>

      {/* Modal Confirmation */}
      <CommonStyles.ModalConfirmationUI
        title={t("Logout") + "?"}
        okText={t("Logout")}
        content={t("ConfirmLogoutContent") + "?"}
        onConfirm={onLogout}
        open={open}
        toggle={toggle}
      />

      <div className="xl:hidden">
        <FloatButton.Group trigger="click" type="primary" style={{ right: 15 }}>
          <FloatButton description={<CommonIcons.IconProfile />} onClick={() => navigate(baseRoutes.userProfile)} tooltip={t("user.menu.profile")} />
          <FloatButton
            description={<CommonIcons.IconCalendarFill />}
            onClick={() => navigate(baseRoutes.userAppointment)}
            tooltip={t("user.menu.appointment")}
          />
          <FloatButton
            description={<CommonIcons.IconHealthcare />}
            onClick={() => navigate(baseRoutes.userPackage)}
            tooltip={t("user.menu.package")}
          />
          <FloatButton
            description={<CommonIcons.IconInsurance />}
            onClick={() => navigate(baseRoutes.userInsurance)}
            tooltip={t("user.menu.insurance")}
          />
          <FloatButton
            description={<CommonIcons.IconTextSnippet />}
            onClick={() => navigate(baseRoutes.userExamination)}
            tooltip={t("user.menu.examination")}
          />
          <FloatButton
            description={<CommonIcons.IconMonitorHeart />}
            onClick={() => navigate(baseRoutes.userChronic)}
            tooltip={t("user.menu.chronic")}
          />
          <FloatButton description={<CommonIcons.IconHistory />} onClick={() => navigate(baseRoutes.userPayment)} tooltip={t("user.menu.payment")} />
          <FloatButton
            description={<CommonIcons.IconLock />}
            onClick={() => navigate(baseRoutes.userChangePassword)}
            tooltip={t("user.menu.changePassword")}
          />
          <FloatButton description={<CommonIcons.IconLogout />} onClick={openLogoutConfirm} tooltip={t("user.menu.logout")} />
        </FloatButton.Group>
      </div>
    </div>
  );
}
